import { useCallback, useEffect, useRef, useState } from "react";
import { qaReportsApi } from "./qaReportsApi";
import type { QaReport, ReportError } from "./qaReportTypes";

type ShowError = (error: ReportError) => void;
type PendingDelay = { timer: ReturnType<typeof setTimeout>; resolve: () => void };
const pending = (report: QaReport) => report.status === "queued" || report.status === "processing";

export function useQaReportHistory(logout: () => void, showError: ShowError) {
  const [recentReports, setRecentReports] = useState<QaReport[]>([]);
  const [recentReportsLoading, setRecentReportsLoading] = useState(true);
  const [recentReportsError, setRecentReportsError] = useState<string | null>(null);
  const [statusErrors, setStatusErrors] = useState<Record<string, string>>({});
  const [retryingIds, setRetryingIds] = useState<Set<string>>(() => new Set());
  const activeRetries = useRef(new Set<string>());
  const polls = useRef(new Map<string, Promise<QaReport | null>>());
  const delays = useRef(new Set<PendingDelay>());
  const mounted = useRef(true);
  const generation = useRef(0);
  const callbacks = useRef({ logout, showError });
  callbacks.current = { logout, showError };

  useEffect(() => {
    mounted.current = true;
    const version = generation.current;
    const activeDelays = delays.current;
    const activePolls = polls.current;
    const retries = activeRetries.current;
    return () => {
      mounted.current = false;
      generation.current = version + 1;
      // Resolve waiting polls as well as clearing timers, so callers can finish.
      activeDelays.forEach(({ timer, resolve }) => { clearTimeout(timer); resolve(); });
      activeDelays.clear();
      activePolls.clear();
      retries.clear();
    };
  }, []);

  const upsertReport = useCallback((report: QaReport) => {
    if (!mounted.current) return;
    setRecentReports((current) => current.some((item) => item.id === report.id)
      ? current.map((item) => item.id === report.id ? report : item)
      : [report, ...current]);
  }, []);

  const pollReport = useCallback((initial: QaReport): Promise<QaReport | null> => {
    const existing = polls.current.get(initial.id);
    if (existing) return existing;
    const version = generation.current;
    const isCurrent = () => mounted.current && version === generation.current;
    const task = (async () => {
      let report = initial;
      while (isCurrent() && pending(report)) {
        await new Promise<void>((resolve) => {
          const delay: PendingDelay = {
            resolve,
            timer: setTimeout(() => { delays.current.delete(delay); resolve(); }, 2000),
          };
          delays.current.add(delay);
        });
        if (!isCurrent()) return null;
        try {
          const response = await qaReportsApi.status(report.status_url);
          if (!isCurrent()) return null;
          if (!response.success) {
            if (response.errorStatus === 401) callbacks.current.logout();
            throw new Error(response.data.message || "Could not check report status. Refresh Recent reports to try again.");
          }
          report = response.data;
        } catch (error) {
          if (!isCurrent()) return null;
          setStatusErrors((current) => ({ ...current, [initial.id]: "Could not update some report statuses. Check your connection and refresh the page." }));
          throw error;
        }
        setStatusErrors((current) => {
          const next = { ...current };
          delete next[initial.id];
          return next;
        });
        upsertReport(report);
      }
      return isCurrent() ? report : null;
    })();
    polls.current.set(initial.id, task);
    const remove = () => {
      if (polls.current.get(initial.id) === task) polls.current.delete(initial.id);
    };
    // Both handlers consume the cleanup promise; failures still reach callers.
    void task.then(remove, remove);
    return task;
  }, [upsertReport]);

  const downloadReport = useCallback(async (report: QaReport): Promise<void> => {
    if (!report.download_url || !mounted.current) return;
    try {
      const response = await qaReportsApi.download(report);
      if (!mounted.current || response.success) return;
      if (response.errorStatus === 401) callbacks.current.logout();
      callbacks.current.showError({ title: response.data.message || "Could not download the report" });
    } catch {
      if (mounted.current) callbacks.current.showError({ title: "Could not download the report", subtitle: "Check your connection and try again." });
    }
  }, []);

  const waitAndDownload = useCallback(async (report: QaReport) => {
    const completed = await pollReport(report);
    if (!completed) return;
    if (completed.status === "completed") await downloadReport(completed);
    else if (completed.status === "failed") {
      throw new Error(completed.error_message || "Report generation failed. Try generating it again.");
    }
  }, [pollReport, downloadReport]);

  const loadRecentReports = useCallback(async () => {
    const version = generation.current;
    setRecentReportsLoading(true);
    setRecentReportsError(null);
    try {
      const response = await qaReportsApi.history();
      if (!mounted.current || version !== generation.current) return;
      if (!response.success) {
        if (response.errorStatus === 401) callbacks.current.logout();
        else if (response.errorStatus !== 404) throw new Error("History unavailable");
        return;
      }
      setRecentReports(response.data.data);
      setStatusErrors({});
      response.data.data.filter(pending).forEach((report) => {
        // Polling records its own status error; history remains usable.
        void pollReport(report).catch(() => {});
      });
    } catch {
      if (mounted.current && version === generation.current) {
        setRecentReportsError("Could not load recent reports. Refresh the page to try again.");
      }
    } finally {
      if (mounted.current && version === generation.current) setRecentReportsLoading(false);
    }
  }, [pollReport]);

  const retryReport = useCallback(async (report: QaReport) => {
    if (activeRetries.current.has(report.id)) return;
    activeRetries.current.add(report.id);
    setRetryingIds(new Set(activeRetries.current));
    try {
      const response = await qaReportsApi.retry(report.id);
      if (!mounted.current) return;
      if (!response.success) {
        if (response.errorStatus === 401) callbacks.current.logout();
        throw new Error(response.data.message || "Could not generate the report again");
      }
      upsertReport(response.data);
      await waitAndDownload(response.data);
    } catch (error) {
      if (mounted.current) callbacks.current.showError({ title: error instanceof Error ? error.message : "Could not generate the report again" });
    } finally {
      activeRetries.current.delete(report.id);
      if (mounted.current) setRetryingIds(new Set(activeRetries.current));
    }
  }, [upsertReport, waitAndDownload]);

  return {
    recentReports, recentReportsLoading, recentReportsError, retryingIds,
    recentReportsUpdateError: Object.keys(statusErrors).length > 0 ? Object.values(statusErrors)[0] : null,
    loadRecentReports, upsertReport, pollReport, waitAndDownload, downloadReport, retryReport,
  };
}
